import React, { useState } from "react";
import styled from "@emotion/styled";

import Collapsible from "react-collapsible";
import { useIntl } from "react-intl";

import ParkkiTheme from "themes/ParkkiTheme";
import MapConfig from "services/map/MapConfig";
import ExpandableStatsHeader from "components/ExpandableStatsHeader";

const Container = styled.div`
  border-bottom: 1px solid ${ParkkiTheme.colors.darkGrey};
  padding-bottom: ${ParkkiTheme.spacings.xsmall};
  padding-top: ${ParkkiTheme.spacings.base};
  margin-right: ${ParkkiTheme.spacings.base};
  margin-left: ${ParkkiTheme.spacings.base};
`;

const ContentRowContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  margin-top: ${ParkkiTheme.spacings.small};
  color: ${ParkkiTheme.colors.darkGrey};
  font-size: ${ParkkiTheme.fontSizes.xsmall};
`;

const ValueLabel = styled.span`
  font-weight: 600;
  font-size: ${ParkkiTheme.fontSizes.small};
`;

type Props = {
  disabled: boolean;
  byPercentage: boolean;
  stats: {
    short_name: string;
    totalMouvements: { value: string; percentage: number };
    options: { selection_color: string };
    stats: [
      {
        type: string;
        percentage: number;
        value: number;
      }
    ];
  };
};

function ExpandableStats({
  disabled,
  byPercentage,
  stats,
}: Props): JSX.Element {
  const intl = useIntl();
  const [headerRowOpen, setHeaderRowOpen] = useState(false);

  function getFormattedPercentage(percentage: number) {
    return Math.round((percentage || 0) * 100);
  }

  function handleOpening() {
    setHeaderRowOpen(true);
  }
  function handleClosing() {
    setHeaderRowOpen(false);
  }

  return (
    <Container>
      <Collapsible
        triggerDisabled={disabled}
        onOpening={handleOpening}
        onClosing={handleClosing}
        trigger={
          <ExpandableStatsHeader
            disabled={disabled}
            label={stats?.short_name || ""}
            value={
              byPercentage
                ? `${getFormattedPercentage(
                    stats?.totalMouvements?.percentage
                  )} %`
                : stats?.totalMouvements?.value
            }
            color={
              stats?.options?.selection_color ||
              MapConfig?.flow?.point?.fillColor?.selected ||
              "#69A73B"
            }
            open={headerRowOpen}
          />
        }
      >
        <ContentRowContainer>
          <div>
            {(stats?.stats || [])
              ?.filter((item) => item?.percentage > 0)
              ?.map((item, key) => {
                return (
                  <div key={`${item.percentage}-${key}`}>
                    <ValueLabel>
                      {byPercentage
                        ? `${item.percentage} % `
                        : `${item.value} `}
                    </ValueLabel>
                    {byPercentage ? (
                      intl.formatMessage(
                        {
                          id: "flowScene.inOutDistribution.expandableStats.content",
                        },
                        {
                          type: intl.formatMessage(
                            {
                              id: `flowScene.flowType.${(
                                item?.type ?? ""
                              ).toLowerCase()}`,
                            },
                            { count: 2 }
                          ),
                        }
                      )
                    ) : (
                      <>
                        {intl.formatMessage(
                          {
                            id: `flowScene.flowType.${(
                              item?.type ?? ""
                            ).toLowerCase()}`,
                          },
                          { count: item?.value ?? 2 }
                        )}
                      </>
                    )}
                  </div>
                );
              })}
          </div>
        </ContentRowContainer>
      </Collapsible>
    </Container>
  );
}

export default ExpandableStats;
